/* eslint-disable react/function-component-definition */
// src/layouts/account/accountPurchaseTallyData.js
import { useState } from "react";
import api from "api/api";

const EXCLUDED_ALL_TYPES = new Set(["1000", "1002", "1003", "1008"]);
const WELLSTORY_TYPES = new Set(["1", "2", "3", "4"]);

// 숫자 파싱
const parseNumber = (value) => {
  if (!value) return 0;
  return Number(String(value).replace(/,/g, "")) || 0;
};

// 숫자 포맷
const formatNumber = (value) => {
  if (!value && value !== 0) return "";
  return Number(value).toLocaleString();
};

// 상세 응답에서 필드명이 다를 수 있어 대소문자 구분 없이 값을 조회
const pickField = (obj, keys) => {
  if (!obj || typeof obj !== "object") return "";

  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }

  const entries = Object.entries(obj);
  for (const key of keys) {
    const found = entries.find(([k]) => String(k).toLowerCase() === String(key).toLowerCase());
    if (found && found[1] !== undefined && found[1] !== null) return found[1];
  }

  return "";
};

const hasValue = (v) => !(v === null || v === undefined || String(v).trim() === "");

const normalizeItemType = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "1" || s.includes("식재료")) return "1";
  if (s === "2" || s.includes("소모품")) return "2";
  if (s === "3" || s.includes("경관식")) return "3";
  return "";
};

const normalizeTaxType = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "1" || s.includes("과세")) return "1";
  if (s === "2" || s.includes("면세")) return "2";
  return "";
};

// 상세(itemtype/taxtype) 기준으로 상단 집계 컬럼을 계산
const summarizeTallyByDetail = (detailRows) => {
  const sum = {
    expen_tax: 0,
    expen_vat: 0,
    expen_taxFree: 0,
    expen_total: 0,
    food_tax: 0,
    food_vat: 0,
    food_taxFree: 0,
    food_total: 0,
    scenic_tax: 0,
    scenic_vat: 0,
    scenic_taxFree: 0,
    scenic_total: 0,
    expen_item_rows: 0,
    food_item_rows: 0,
    scenic_item_rows: 0,
    expen_tax_rows: 0,
    food_tax_rows: 0,
    scenic_tax_rows: 0,
  };

  (detailRows || []).forEach((item) => {
    const itemType = normalizeItemType(pickField(item, ["itemType", "itemtype", "item_type"]));
    const taxType = normalizeTaxType(pickField(item, ["taxType", "taxtype", "tax_type"]));

    // itemtype: 1=식재료, 2=소모품, 3=경관식
    if (itemType !== "1" && itemType !== "2" && itemType !== "3") return;

    const amountRaw = pickField(item, ["amount", "total_amount", "totalAmount"]);
    const taxRaw = pickField(item, ["tax", "tax_amount", "supply", "supply_amount"]);
    const vatRaw = pickField(item, ["vat", "vat_amount"]);
    const qtyRaw = pickField(item, ["qty", "quantity"]);
    const unitPriceRaw = pickField(item, ["unitPrice", "unitprice", "unit_price"]);
    const qty = parseNumber(qtyRaw);
    const unitPrice = parseNumber(unitPriceRaw);
    const amount = hasValue(amountRaw)
      ? parseNumber(amountRaw)
      : (qty > 0 ? qty * unitPrice : unitPrice);

    const prefix = itemType === "1" ? "food" : itemType === "3" ? "scenic" : "expen";
    sum[`${prefix}_item_rows`] += 1;

    // taxtype: 1=과세, 2=면세
    if (taxType === "1") {
      // 과세는 detail의 tax/vat/amount를 그대로 사용하고, 누락 시에만 amount 기반으로 보정한다.
      const hasTaxInput = hasValue(taxRaw);
      const hasVatInput = hasValue(vatRaw);
      const hasAmountInput = hasValue(amountRaw);
      let tax = hasTaxInput ? parseNumber(taxRaw) : 0;
      let vat = hasVatInput ? parseNumber(vatRaw) : 0;
      let total = hasAmountInput ? parseNumber(amountRaw) : 0;

      if (!hasTaxInput && !hasVatInput) {
        const autoVat = Math.floor(amount / 11);
        const autoTax = Math.max(amount - autoVat, 0);
        tax = autoTax;
        vat = autoVat;
        total = amount;
      } else if (!hasTaxInput && hasAmountInput && amount >= vat) {
        tax = Math.max(amount - vat, 0);
      } else if (!hasVatInput && hasAmountInput && amount >= tax) {
        vat = Math.max(amount - tax, 0);
      }

      if (!hasAmountInput) {
        total = tax + vat;
      }

      sum[`${prefix}_tax_rows`] += 1;
      sum[`${prefix}_tax`] += tax;
      sum[`${prefix}_vat`] += vat;
      sum[`${prefix}_total`] += total;
      return;
    }

    if (taxType === "2") {
      sum[`${prefix}_tax_rows`] += 1;
      sum[`${prefix}_taxFree`] += amount;
      sum[`${prefix}_total`] += amount;
      return;
    }

    // 알수없음 등 기타 타입은 합계에만 반영
    sum[`${prefix}_total`] += amount;
  });

  return sum;
};

export default function useAccountPurchaseTallyData() {
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);

  const [partnerList, setPartnerList] = useState([]);
  const [mappingRows, setMappingRows] = useState([]);

  const [loading, setLoading] = useState(false);

  /**
   * 매입 집계 조회
   * - AccountPurchaseTallyForTallyTab API 사용
   * - fromMonth(YYYY-MM)~toMonth(YYYY-MM) 범위 조회 지원
   * - toMonth 없으면 fromMonth 단일 월 조회
   * @param {Object} filters - { type, fromMonth(YYYY-MM), toMonth(YYYY-MM), account_id, ... }
   */
  const fetchPurchaseList = async (filters) => {
    setLoading(true);
    try {
      const toParam = (v) => (!v || v === "0" ? "" : v);
      const selectedRawType = String(filters?.rawType ?? filters?.type ?? "0");
      const isAllTypeQuery = selectedRawType === "0" || selectedRawType === "";
      const isWellstoryQuery = selectedRawType === "wellstory";
      const year = toParam(filters?.year);
      const fromMonth = toParam(filters?.fromMonth); // 월 숫자 문자열
      const toMonth = toParam(filters?.toMonth);     // 월 숫자 문자열 (optional)

      let startDate = "";
      let endDate = "";
      if (year && fromMonth) {
        const y = Number(year);
        const fm = Number(fromMonth);
        startDate = `${year}-${String(fm).padStart(2, "0")}-01`;
        if (toMonth) {
          const tm = Number(toMonth);
          const next = new Date(y, tm, 1); // tm월 다음달 1일
          endDate = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-01`;
        } else {
          const next = new Date(y, fm, 1);
          endDate = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-01`;
        }
      } else if (year) {
        startDate = `${year}-01-01`;
        endDate = `${Number(year) + 1}-01-01`;
      }

      const params = {
        account_id: toParam(filters?.account_id),
        type: toParam(filters?.type),
        startDate,
        endDate,
      };

      const res = await api.get("/Account/AccountPurchaseTallyForTallyTab", { params });

      const list = Array.isArray(res.data) ? res.data : res.data?.rows || [];
      const mappedBase = (list || []).map((item) => ({
        account_id: item.account_id ?? item.accountId ?? "",
        account_name: item.account_name || "",
        sale_id: item.sale_id ?? item.saleId ?? "",
        type: item.type ?? item.mapping_type ?? "",
        type_name: item.type_name || "",
        saleDate: item.saleDate || item.sale_date || "",
        // 소모품(itemType=2): 과세 공급가 / 부가세 / 면세 / 합계
        expen_tax: formatNumber(item.expen_tax),
        expen_vat: formatNumber(item.expen_vat),
        expen_taxFree: formatNumber(item.expen_taxFree),
        expen_total: formatNumber(item.expen_total),
        // 식재료(itemType=1): 과세 공급가 / 부가세 / 면세 / 합계
        food_tax: formatNumber(item.food_tax),
        food_vat: formatNumber(item.food_vat),
        food_taxFree: formatNumber(item.food_taxFree),
        food_total: formatNumber(item.food_total),
        // 경관식(itemType=3): 과세 공급가 / 부가세 / 면세 / 합계
        scenic_tax: formatNumber(item.scenic_tax),
        scenic_vat: formatNumber(item.scenic_vat),
        scenic_taxFree: formatNumber(item.scenic_taxFree),
        scenic_total: formatNumber(item.scenic_total),
        use_name: item.use_name || "",
        note: item.note || "",
        bizNo: item.bizNo || "",
        ceo_name: item.ceo_name || "",
        reg_dt: item.reg_dt || "",
      }));

      // 전체/삼성웰스토리 선택값에 맞춰 화면 표시 대상 타입만 남긴다.
      const applyTypeVisibility = (targetRows) => {
        if (isWellstoryQuery) {
          return (targetRows || []).filter((row) => WELLSTORY_TYPES.has(String(row?.type ?? "")));
        }

        if (isAllTypeQuery) {
          return (targetRows || []).filter((row) => !EXCLUDED_ALL_TYPES.has(String(row?.type ?? "")));
        }

        return targetRows || [];
      };

      const hasSaleIdRow = mappedBase.some((row) => String(row?.sale_id ?? "").trim() !== "");
      if (!hasSaleIdRow) {
        const visibleBaseRows = applyTypeVisibility(mappedBase);
        setRows(visibleBaseRows);
        setOriginalRows(visibleBaseRows.map((r) => ({ ...r })));
        return;
      }

      // ✅ 성능 최적화:
      // - 전체 조회(account_id 미선택)에서 행 수가 많으면 상세 API N회 호출이 매우 느려진다.
      // - 이 경우 서버 집계(AccountPurchaseTallyForTallyTab) 값을 그대로 사용한다.
      const isAllAccountQuery = String(toParam(filters?.account_id)) === "";
      const isBroadQuery = isAllAccountQuery && isAllTypeQuery;
      const useDetailRecalc = !isBroadQuery && (!isAllAccountQuery || mappedBase.length <= 300);
      if (!useDetailRecalc) {
        const visibleBaseRows = applyTypeVisibility(mappedBase);
        setRows(visibleBaseRows);
        setOriginalRows(visibleBaseRows.map((r) => ({ ...r })));
        return;
      }

      // sale_id 기준으로 상세를 조회해 itemtype/taxtype 집계값을 우선 반영
      // 5개씩 청크 단위 병렬 조회
      const CHUNK_SIZE = 5;
      const detailSummaryCache = new Map();

      const uniqueSaleIds = [...new Set(
        mappedBase.map((r) => String(r?.sale_id ?? "").trim()).filter(Boolean)
      )];

      for (let i = 0; i < uniqueSaleIds.length; i += CHUNK_SIZE) {
        const chunk = uniqueSaleIds.slice(i, i + CHUNK_SIZE);
        await Promise.all(
          chunk.map((saleId) => {
            if (detailSummaryCache.has(saleId)) return Promise.resolve();
            const req = api
              .get("/Account/AccountPurchaseDetailList_tmp", { params: { sale_id: saleId } })
              .then((r) => {
                const detailList = Array.isArray(r.data) ? r.data : r.data?.rows || r.data?.data || [];
                return detailList.length > 0 ? summarizeTallyByDetail(detailList) : null;
              })
              .catch((err) => {
                console.error("매입 상세 집계 조회 실패:", err);
                return null;
              });
            detailSummaryCache.set(saleId, req);
            return req;
          })
        );
      }

      const mapped = [];
      for (const row of mappedBase) {
        const saleId = String(row?.sale_id ?? "").trim();
        if (!saleId) {
          mapped.push(row);
          continue;
        }

        const summary = await detailSummaryCache.get(saleId);
        if (!summary) {
          mapped.push(row);
          continue;
        }

        mapped.push({
          ...row,
          // ✅ 소모품: itemType=2 상세가 있을 때만 total 반영
          expen_total:
            summary.expen_item_rows > 0 ? formatNumber(summary.expen_total) : row.expen_total,
          // ✅ 소모품 과세/면세/부가세도 상세가 있으면 동일 기준으로 반영
          expen_tax:
            summary.expen_item_rows > 0 ? formatNumber(summary.expen_tax) : row.expen_tax,
          expen_vat:
            summary.expen_item_rows > 0 ? formatNumber(summary.expen_vat) : row.expen_vat,
          expen_taxFree:
            summary.expen_item_rows > 0 ? formatNumber(summary.expen_taxFree) : row.expen_taxFree,

          // ✅ 식재료: itemType=1 상세가 있을 때만 total 반영
          food_total:
            summary.food_item_rows > 0 ? formatNumber(summary.food_total) : row.food_total,
          // ✅ 식재료 과세/면세/부가세도 상세가 있으면 동일 기준으로 반영
          food_tax:
            summary.food_item_rows > 0 ? formatNumber(summary.food_tax) : row.food_tax,
          food_vat:
            summary.food_item_rows > 0 ? formatNumber(summary.food_vat) : row.food_vat,
          food_taxFree:
            summary.food_item_rows > 0 ? formatNumber(summary.food_taxFree) : row.food_taxFree,

          // ✅ 경관식: itemType=3 상세가 있을 때만 반영
          scenic_total:
            summary.scenic_item_rows > 0 ? formatNumber(summary.scenic_total) : row.scenic_total,
          scenic_tax:
            summary.scenic_item_rows > 0 ? formatNumber(summary.scenic_tax) : row.scenic_tax,
          scenic_vat:
            summary.scenic_item_rows > 0 ? formatNumber(summary.scenic_vat) : row.scenic_vat,
          scenic_taxFree:
            summary.scenic_item_rows > 0 ? formatNumber(summary.scenic_taxFree) : row.scenic_taxFree,
        });
      }

      const visibleMappedRows = applyTypeVisibility(mapped);
      setRows(visibleMappedRows);
      setOriginalRows(visibleMappedRows.map((r) => ({ ...r })));
    } catch (err) {
      console.error("매입 집계 조회 실패:", err);
      setRows([]);
      setOriginalRows([]);
      setPartnerList([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * ✅ 구매처 매핑 리스트
   * - 이 값이 구매처 select 구성값이 됨
   * - [{type, name}, ...]
   */
  const fetchMappingList = async (filters) => {
    try {
      // account_id가 있으면 해당 거래처 매핑(V2), 없으면 전체 매핑 목록
      const hasAccountId = filters?.account_id && String(filters.account_id).trim() !== "";
      const url = hasAccountId ? "/Operate/AccountMappingV2List" : "/Operate/AccountMappingList";

      const res = await api.get(url, {
        params: hasAccountId
          ? { account_id: filters.account_id, _ts: Date.now() }
          : { _ts: Date.now() },
      });

      const list = Array.isArray(res.data) ? res.data : res.data?.rows || res.data?.data || [];

      const mapped = (list || [])
        .filter((item) => String(item?.del_yn ?? "N") !== "Y")
        .map((item) => ({
          type: item.type,
          name: item.name,
        }));

      const has1002 = mapped.some((item) => String(item?.type ?? "") === "1002");
      const has1003 = mapped.some((item) => String(item?.type ?? "") === "1003");
      const blocked1002 = (list || []).some(
        (item) => String(item?.type ?? "") === "1002" && String(item?.del_yn ?? "N") === "Y"
      );
      const blocked1003 = (list || []).some(
        (item) => String(item?.type ?? "") === "1003" && String(item?.del_yn ?? "N") === "Y"
      );

      if (!has1002 && !blocked1002) {
        mapped.push({ type: "1002", name: "기타경비" });
      }
      if (!has1003 && !blocked1003) {
        mapped.push({ type: "1003", name: "기타" });
      }

      mapped.sort((a, b) => {
        const an = Number(a?.type);
        const bn = Number(b?.type);
        const aNum = Number.isFinite(an);
        const bNum = Number.isFinite(bn);
        if (aNum && bNum) return an - bn;
        return String(a?.type ?? "").localeCompare(String(b?.type ?? ""), "ko");
      });

      setMappingRows(mapped.map((r) => ({ ...r })));
    } catch (err) {
      console.error("AccountMappingList 조회 실패:", err);
      setMappingRows([]);
    }
  };

  // 거래처 목록 조회 API
  const fetchAccountList = async (params = { account_type: "0" }) => {
    const res = await api.get("/Account/AccountListV2", {
      params: { del_yn: "ALL", ...params },
    });
    return Array.isArray(res?.data) ? res.data : res?.data?.rows || res?.data?.data || [];
  };

  // 거래처 기준 타입 옵션 조회 API
  const fetchTypeOptionList = async (accountId) => {
    if (!accountId) return [];
    const res = await api.get("/Operate/AccountMappingV2List", {
      params: { account_id: accountId, _ts: Date.now() },
    });
    return res?.data;
  };

  return {
    rows,
    setRows,
    originalRows,
    mappingRows,
    partnerList,
    loading,
    fetchPurchaseList,
    fetchMappingList,
    fetchAccountList,
    fetchTypeOptionList,
  };
}

export { parseNumber, formatNumber };
